import {
  getPageContentHtml,
  gtcSetPreviousFocus,
  gtcTrapFocus,
  gtcRemoveTrapFocus,
} from "gtcUtils";

let termsModalRendered = false;

class GTCTerms extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.settings = {
      ...JSON.parse(this.getAttribute("settings")),
    };

    this.dom = {
      main: this,
      checkBox: this.querySelector("#GtcTermsCheck"),
    };

    this.cartParent = null;
    for (let selector of this.settings.parentSelectors) {
      this.cartParent = this.dom.main.closest(selector);
      if (this.cartParent) {
        break;
      }
    }

    this.cartForm = this.cartParent.querySelector('form[action*="/cart"]');
    
    this.cartSubmit = this.cartParent.querySelector(
      this.settings.submitSelector
    );

    this.cartSubmitDefaultClasses = this.cartSubmit.classList.value;

    this.AddListeners();
  }

  disconnectedCallback() {
    this.RemoveListeners();
  }

  AddListeners() {
    this.addEventListener("click", this.Click);

    this.cartForm.addEventListener("submit", (e) => {
      this.Validate(e);
    });

    document.addEventListener("terms:accepted", () => {
      this.AcceptAndSubmit();
    });

    document.addEventListener("terms:modal:close", () => {
      this.cartSubmit.setAttribute("aria-busy", false);
    });
  }

  RemoveListeners() {
    this.removeEventListener("click", this.Click);
    this.cartForm.removeEventListener("submit", this.Validate);
    document.removeEventListener("terms:accepted", this.AcceptAndSubmit);
    document.removeEventListener("terms:modal:close", this.cartSubmit);
  }

  AcceptAndSubmit() {
    this.dom.checkBox.checked = true;
    this.cartSubmit.click();
  }

  Validate(e) {
    if (!this.dom.checkBox.checked) {
      e.preventDefault();
      setTimeout(() => {
        this.cartSubmit.classList.value = this.cartSubmitDefaultClasses;
      }, 0);

      if (termsModalRendered) {
        this.ModalOpen({ expandOnLoad: false });
      } else {
        this.CreateModal({ expandOnLoad: false });
      }
    }
  }

  CreateModal({ expandOnLoad }) {
    const termsModalTemplate = document.querySelector(
      "#gtc-terms-modal-template"
    );
    this.termsModal = termsModalTemplate.content.cloneNode(true);
    this.cartParent.appendChild(this.termsModal);

    termsModalRendered = true;
    this.ModalOpen({ expandOnLoad });
  }

  ModalOpen({ expandOnLoad }) {
    gtcSetPreviousFocus(this.dom.checkBox);
    document.dispatchEvent(
      new CustomEvent("terms:modal:open", {
        detail: {
          expandOnLoad: expandOnLoad,
        },
      })
    );
  }

  Click(event) {
    if (event.target.getAttribute("href") == this.settings.url) {
      event.preventDefault();
      setTimeout(() => {
        if (termsModalRendered) {
          this.ModalOpen({ expandOnLoad: true });
        } else {
          this.CreateModal({ expandOnLoad: true });
        }
      }, 0);
    }
  }
}
customElements.define("gtc-terms", GTCTerms);

class GTCTermsModal extends HTMLElement {
  static get observedAttributes() {
    return ["open", "expanded"];
  }
  constructor() {
    super();
  }

  connectedCallback() {
    this.settings = {
      ...JSON.parse(this.getAttribute("settings")),
      expandedTextLoaded: false,
    };

    this.dom = {
      main: this,
      expandedContent: this.querySelector("[data-expanded-content]"),
      termsSection: this.querySelector("[data-terms-content-terms]"),
    };

    // Forces repaint, so it animates nicely
    void this.offsetHeight;

    this.Binding();
    this.AddListeners();

    // This eventlistener should not be removed
    document.addEventListener("terms:modal:open", this.Open);
  }

  disconnectedCallback() {
    this.RemoveListeners();

    document.removeEventListener("terms:modal:open", this.Open);

    // If the modal is removed from the DOM, the termsModalRendered flag should be reset
    if (!document.contains(this)) {
      termsModalRendered = false;
    }
  }

  Binding() {
    this.Keydown = this.Keydown.bind(this);
    this.Open = this.Open.bind(this);
    this.Click = this.Click.bind(this);
  }

  AddListeners() {
    document.addEventListener("click", this.Click);
    document.addEventListener("keydown", this.Keydown);
  }

  RemoveListeners() {
    document.removeEventListener("click", this.Click);
    document.removeEventListener("keydown", this.Keydown);
  }

  Click(event) {
    if (event.target.getAttribute("terms") == "accept") {
      document.dispatchEvent(new CustomEvent("terms:accepted"));
      this.Close();
    } else if (
      event.target.getAttribute("terms") == "decline" ||
      !this.dom.main.contains(event.target)
    ) {
      this.Close();
    } else if (event.target.getAttribute("data-button") === "close") {
      this.Close();
    } else if (event.target.getAttribute("href") == this.settings.url) {
      this.ExpandText(event);
    } else if (event.target.getAttribute("data-button") === "expanded-close") {
      this.CloseText();
    }
  }

  GetAnimationDuration(elem) {
    const ms = window.getComputedStyle(elem).transitionDuration.includes("ms");

    return (
      parseFloat(window.getComputedStyle(elem).transitionDuration) *
      (!ms ? 1000 : 1)
    );
  }

  OpenText() {
    this.dom.termsSection.parentElement.style.visibility = "visible";
    this.dom.termsSection.setAttribute("aria-expanded", true);
    this.setAttribute("expanded", true);
  }

  CloseText() {
    const transitionSpeed = this.GetAnimationDuration(
      this.dom.termsSection.parentElement
    );
    this.setAttribute("expanded", false);
    this.dom.termsSection.setAttribute("aria-expanded", false);

    setTimeout(() => {
      this.dom.termsSection.parentElement.style.visibility = "hidden";
    }, transitionSpeed);

    gtcTrapFocus(this.dom.main);
  }

  Open(event) {
    document.body.classList.add("gtc:scroll-lock");
    this.setAttribute("open", "");
    this.AddListeners();

    if (event.detail.expandOnLoad === true) {
      this.ExpandText(event);
    }

    gtcTrapFocus(this.dom.main);
  }

  Close() {
    document.body.classList.remove("gtc:scroll-lock");
    this.removeAttribute("open", "");
    this.setAttribute("aria-busy", false);
    this.RemoveListeners();
    this.CloseText();
    document.dispatchEvent(new CustomEvent("terms:modal:close"));

    gtcRemoveTrapFocus(this.dom.main);
  }

  Keydown(event) {
    // If Esc key
    if (event.keyCode !== 27) return;
    this.Close();
  }

  ExpandText(event) {
    event.preventDefault();
    if (this.hasAttribute("expanded-text-loaded")) {
      this.OpenText();
    } else {
      this.FetchFullTermsText();
    }
  }

  async FetchFullTermsText() {
    const pageContent = await getPageContentHtml(this.settings.url);

    this.dom.expandedContent.insertAdjacentHTML("afterbegin", pageContent);
    this.setAttribute("expanded-text-loaded", "");
    this.OpenText();
  }
}
customElements.define("gtc-terms-modal", GTCTermsModal);
